import $ from "jquery";
import { AbstractComponent } from "../../AbstractComponent";
import { LightboxComponent } from "../../components/LightboxComponent";
import { replacePlaceholders } from "../../toolkit";

export interface VerifyIdentityConfig {
    /**
     * Whether to automatically advance to the next input field when a digit is entered.
     * Also enables moving back to previous field on backspace.
     */
    autoAdvance: boolean;

    /**
     * The input mode for the verification code.
     * - "individual": Six separate input fields (one per digit)
     * - "single": One input field for the entire code
     */
    inputMode: "individual" | "single";

    /**
     * Optional callback to handle successful verification.
     * Receives the API response data.
     */
    onVerifySuccess?: (data: any) => void;

    /**
     * Function to send the verification code to the user.
     * Should return a promise that resolves when the code is sent.
     */
    onSendCode?: (email: string) => Promise<any>;

    /**
     * Function to verify the code entered by the user.
     * Should return a promise that resolves with the verification response.
     */
    onVerifyCode?: (email: string, code: string) => Promise<any>;

    /**
     * Function to resend the verification code.
     * Should return a promise that resolves when the code is resent.
     */
    onResendCode?: (email: string) => Promise<any>;
}

export abstract class BaseVerifyIdentityComponent extends AbstractComponent {
    protected email: string = "";
    protected inputs: JQuery;
    protected submitButton: JQuery;
    protected resendButton: JQuery;
    protected backButton: JQuery;
    protected codeDisplay: JQuery;
    protected errorDisplay: JQuery;
    protected config: VerifyIdentityConfig;

    public init(): void {
        // Set default config
        this.config = {
            autoAdvance: true,
            inputMode: "individual",
            ...this.getConfig()
        };

        // Find elements
        this.inputs = this.componentElement.find("input[name^='code_']");
        this.submitButton = this.componentElement.find("[data-submit-verification]");
        this.resendButton = this.componentElement.find("[data-resend-verification]");
        this.backButton = this.componentElement.find("[data-back]");
        this.codeDisplay = this.componentElement.find(".verify-identity__header span");
        this.errorDisplay = this.componentElement.find("[data-error]");

        this.initializeEventHandlers();
    }

    protected abstract getConfig(): Partial<VerifyIdentityConfig>;

    protected initializeEventHandlers(): void {
        // Input handling
        this.inputs.on("input", (event) => this.handleInput(event));
        this.inputs.on("keydown", (event) => this.handleKeydown(event));
        this.inputs.on("paste", (event) => this.handlePaste(event));
        this.inputs.on("focus", (event) => this.handleFocus(event));
        this.inputs.on("click", (event) => this.handleClick(event));

        // Button handlers
        this.submitButton.on("click", () => this.handleSubmit());
        this.resendButton.on("click", () => this.handleResend());
        this.backButton.on("click", () => this.handleBack());

        // Form submission
        this.componentElement.find("form").on("submit", (event) => {
            event.preventDefault();
            this.handleSubmit();
        });
    }

    protected handleInput(event: JQuery.TriggeredEvent): void {
        const input = $(event.target);
        const value = input.val() as string;

        // Only allow digits
        if (!/^\d*$/.test(value)) {
            input.val(value.replace(/\D/g, ""));
            return;
        }

        // Auto-advance if enabled
        if (this.config.autoAdvance && value.length === 1) {
            const currentIndex = this.inputs.index(input);
            if (currentIndex < this.inputs.length - 1) {
                this.inputs.eq(currentIndex + 1).focus();
            }
        }

        this.setButtonState();
    }

    protected handleKeydown(event: JQuery.KeyDownEvent): void {
        const input = $(event.target);
        const key = event.key;
        const currentIndex = this.inputs.index(input);
        const currentValue = input.val() as string;

        if (key === "Backspace") {
            if (currentValue === "" && this.config.autoAdvance && currentIndex > 0) {
                event.preventDefault();
                // Clear the previous input and move focus to it
                const prevInput = this.inputs.eq(currentIndex - 1);
                prevInput.val("");
                prevInput.focus();
            }
        } else if (key === "Enter" && this.areAllInputsFilled()) {
            event.preventDefault();
            this.handleSubmit();
        }
    }

    protected handlePaste(event: JQuery.TriggeredEvent): void {
        event.preventDefault();
        const pastedData = (event.originalEvent as ClipboardEvent).clipboardData?.getData("text") || "";
        const pastedCode = pastedData.replace(/\D/g, "");

        if (pastedCode.length >= 6) {
            // Distribute the first 6 digits across inputs
            for (let i = 0; i < 6 && i < this.inputs.length; i++) {
                this.inputs.eq(i).val(pastedCode[i]);
            }

            // Focus the last input or submit button
            if (this.areAllInputsFilled()) {
                this.submitButton.prop("disabled", false);
                this.submitButton.focus();
            } else {
                this.inputs.last().focus();
            }
        }
    }

    protected handleFocus(event: JQuery.TriggeredEvent): void {
        const input = $(event.target);
        const currentIndex = this.inputs.index(input);

        // Check if all previous inputs are filled
        if (!this.canFocusInput(currentIndex)) {
            event.preventDefault();
            // Find the first empty input and focus it
            const firstEmptyIndex = this.findFirstEmptyInput();
            if (firstEmptyIndex !== -1) {
                this.inputs.eq(firstEmptyIndex).trigger("focus");
            }
        }
    }

    protected handleClick(event: JQuery.TriggeredEvent): void {
        const input = $(event.target);
        const currentIndex = this.inputs.index(input);

        // Prevent clicking on inputs that shouldn't be accessible yet
        if (!this.canFocusInput(currentIndex)) {
            event.preventDefault();
            const firstEmptyIndex = this.findFirstEmptyInput();
            if (firstEmptyIndex !== -1) {
                this.inputs.eq(firstEmptyIndex).trigger("focus");
            }
        }
    }

    protected canFocusInput(index: number): boolean {
        // Can focus if all previous inputs are filled
        for (let i = 0; i < index; i++) {
            if ((this.inputs.eq(i).val() as string).length === 0) {
                return false;
            }
        }
        return true;
    }

    protected findFirstEmptyInput(): number {
        for (let i = 0; i < this.inputs.length; i++) {
            if ((this.inputs.eq(i).val() as string).length === 0) {
                return i;
            }
        }
        return -1;
    }

    protected areAllInputsFilled(): boolean {
        return this.inputs.toArray().every(input => ($(input).val() as string).length > 0);
    }

    protected getCode(): string {
        return this.inputs.toArray()
            .map(input => $(input).val() as string)
            .join("");
    }

    protected clearInputs(): void {
        this.inputs.val("");
        this.inputs.first().focus();
        this.submitButton.prop("disabled", true);
    }

    protected showError(message: string): void {
        // Use LightboxComponent to display error messages
        LightboxComponent.getDisplay(message, true);
        this.clearInputs();
    }

    protected setEmail(email: string): void {
        this.email = email;
        replacePlaceholders(this.componentElement, { email: email });
    }

    protected async handleSubmit(): Promise<void> {
        if (!this.areAllInputsFilled()) return;

        const code = this.getCode();
        this.submitButton.prop("disabled", true);

        try {
            if (this.config.onVerifyCode) {
                const response = await this.config.onVerifyCode(this.email, code);
                this.handleVerifySuccess(response);
            }
        } catch (error: any) {
            this.handleVerifyError(error);
            this.setButtonState();
        }
    }

    protected async handleResend(): Promise<void> {
        this.resendButton.prop("disabled", true);

        try {
            if (this.config.onResendCode) {
                await this.config.onResendCode(this.email);
                this.showResendSuccess();
            }
        } catch (error) {
            this.showError("Failed to resend code. Please try again.");
        } finally {
            this.resendButton.prop("disabled", false);
        }
    }

    protected handleBack(): void {
        // Default implementation - can be overridden
        window.history.back();
    }

    protected showResendSuccess(): void {
        const originalText = this.resendButton.html();
        this.resendButton.text("Code sent!");
        setTimeout(() => {
            this.resendButton.html(originalText);
        }, 3000);
        this.clearInputs();
        this.setButtonState();
    }

    protected setButtonState(): void {
        this.submitButton.prop("disabled", !this.areAllInputsFilled());
    }

    public initializeWithEmail(email: string): void {
        this.setEmail(email);
        this.clearInputs();

        // Request code is handled by the login.ts file
        // This is for components that just handle verification
    }

    protected abstract handleVerifySuccess(response: any): void;
    protected abstract handleVerifyError(error: any): void;
}
